// @ts-nocheck

/**
 * Extract channels from processed model data and determine maximum values
 * 
 * @param modelKey The key of the processed model
 * @param pModelData The processed model data
 * @returns Array of channel objects with values, labels, and maximum values
 */
/**
 * Extract timeRange from processed model data for a specific channel
 * 
 * @param pModelData The processed model data
 * @param selectedChannel The selected channel (e.g., "Hourly Gen Flow mean")
 * @returns Array of dates for the time slider
 */
export const extractTimeRangeFromProcessedModel = (pModelData: any, selectedChannel: any): Date[] => {
  console.log("Extracting timeRange for channel:", selectedChannel);
  
  if (!pModelData || !selectedChannel) {
    console.warn("Missing data for extracting timeRange", { pModelData, selectedChannel });
    return [];
  }
  
  // Parse the channel to get variation and percentile
  // const parts = selectedChannel.split(' ');
  // const variationChannel = parts.pop(); // Last part is the percentile/channel (mean, 0.1, etc.)
  // const variation = parts.join(' '); // All other parts combined are the variation
  const { variation, variationChannel } = selectedChannel
  
  console.log("Parsed channel parts:", { variation, variationChannel });
  
  // Get all timestamps across all assets
  const allTimestamps = new Set<string>();
  const formattedData = [];
  
  // Iterate through all assets
  Object.values(pModelData).forEach((assetData: any) => {
    if (!assetData?.models) return;
    
    // Find model that matches the variation
    Object.values(assetData.models).forEach((modelData: any) => {
      if (modelData?.variation !== variation || !modelData?.data) return;
      
      // Process each timestamp
      Object.entries(modelData.data).forEach(([timestamp, dataPoint]: [string, any]) => {
        if (!dataPoint || dataPoint[variationChannel] === undefined) return;
        // console.log("timestamp", timestamp)
        try {
          // Parse the timestamp to a Date object
          let dateObj;
          if (timestamp.match(/^\\d{4}-\\d{2}-\\d{2} \\d{2}:\\d{2}:\\d{2}Z?$/)) {
            // Format like "2025-01-22 00:00:00Z"
            dateObj = new Date(timestamp);
          } else {
            // Try other formats or add Z if needed
            dateObj = new Date(timestamp + (timestamp.endsWith('Z') ? '' : 'Z'));
          }
          
          if (!isNaN(dateObj.getTime())) {
            allTimestamps.add(timestamp);
            formattedData.push({
              x: dateObj,
              y: dataPoint[variationChannel],
              assets: { [assetData.assetId]: dataPoint[variationChannel] }
            });
          }
        } catch (e) {
          console.error("Error parsing timestamp:", e);
        }
      });
    });
  });
  
  // Sort timestamps chronologically
  const sortedTimestamps = Array.from(allTimestamps).sort((a, b) => {
    return new Date(a).getTime() - new Date(b).getTime();
  });
  
  // Convert to Date objects
  const timeRange = sortedTimestamps.map(timestamp => new Date(timestamp));
  console.log(`Extracted ${timeRange.length} timestamps for timeRange`);
  
  return timeRange;
};

export const extractProcessedModelChannelsWithMax = (modelKey: string, pModelData: any): any[] => {
  console.log("extractProcessedModelChannelsWithMax called with:", { modelKey, pModelData });
  
  // 1) A Set to ensure each "variation variationChannel" appears only once
  const channelSet = new Set();

  // 2) This array will hold one object per unique channel
  const channels = [];

  // 3) maxValues[channelKey] = current maximum seen for that channel
  const maxValues = {};
  const minValues = {};

  if (!pModelData) {
    console.warn("No pModelData provided to extractProcessedModelChannelsWithMax");
    return [];
  }

  // 4) Loop over all assets in pModelData
  Object.values(pModelData).forEach(assetData => {
    console.log("Processing asset data:", assetData);
    
    if (!assetData?.models) {
      console.warn("Asset data missing models field:", assetData);
      return;
    }

    // 5) Loop over each model under this asset
    Object.values(assetData.models).forEach(modelData => {
      console.log("Processing model data:", modelData);
      
      const variation = modelData?.variation;
      const allTimestamps = modelData?.data;

      if (!variation || !allTimestamps) {
        console.warn("Model data missing variation or data:", { variation, allTimestamps });
        return;
      }

      // 6) Now iterate every timestamp entry
      Object.entries(allTimestamps).forEach(([timestamp, dataPoint]) => {
        // dataPoint might look like { mean: 42.1, "0.1": 30.2, … }
        if (!dataPoint) return;
        
        // Log first timestamp's dataPoint structure to understand format
        if (Object.entries(allTimestamps).length > 0 && timestamp === Object.keys(allTimestamps)[0]) {
          console.log("Sample dataPoint structure:", { timestamp, dataPoint });
        }

        // 7) For each percentile‐key in this timestamp:
        Object.entries(dataPoint).forEach(([variationChannel, rawValue]) => {
          // Build a channel key identical to what you used before:
          const channelKey = `${variation} ${variationChannel}`;

          // If we haven't seen this channelKey before, add it to the Set and to channels[]
          if (!channelSet.has(channelKey)) {
            channelSet.add(channelKey);
            channels.push({
              value: `processed-${channelKey}`,
              label: channelKey,
              source: 'Erode AI',
              dataType: 'processed',
              variation,
              variationChannel,
              unit: modelData?.yUnit
              // We'll fill maxValue later (or you can initialize it now as rawValue)
            });
            
            // Initialize maxValues for this channel with the first rawValue
            maxValues[`processed-${channelKey}`] = rawValue;
            minValues[`processed-${channelKey}`] = rawValue

          } else {
            // If it already exists, compare & update max
            maxValues[`processed-${channelKey}`] = Math.max(maxValues[`processed-${channelKey}`], rawValue);
            minValues[`processed-${channelKey}`] = Math.min(minValues[`processed-${channelKey}`], rawValue)
          }
        });
      });
    });
  });

  // 8) Now that maxValues[...] holds the true max for each channel, merge it back into channels[]
  
  const result = channels.map(channelObj => ({
    ...channelObj,
    maxValue: Math.ceil(maxValues[channelObj.value] / 4) * 4,
    minValue: Math.floor(minValues[channelObj.value] / 4) * 4
  }));
  
  console.log("Final channel options generated:", result);
  return result;
};